import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../contexts/PhpAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../components/ui/use-toast';
import { apiRequest } from '../config/api';
import { Stethoscope, User, Lock, Eye, EyeOff } from 'lucide-react';

const LoginPage = () => {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const { signIn, profile } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await apiRequest('get_login_users.php');
        console.log('Login users response:', data); // Debug log
        if (data.success) {
          setUsers(data.users || []);
          if (!data.users || data.users.length === 0) {
            console.warn('No users found for login dropdown');
          }
        } else {
          console.error('Failed to fetch users:', data.error || data.message);
          toast({
            title: 'Warning',
            description: 'Could not load user list. Please refresh the page.',
            variant: 'destructive'
          });
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        toast({
          title: 'Error',
          description: 'Failed to load users. Please check your connection.',
          variant: 'destructive'
        });
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username || !formData.password) {
      toast({
        title: 'Error',
        description: 'Please select a user and enter password',
        variant: 'destructive'
      });
      return;
    }
    setLoading(true);
    try {
      const { error } = await signIn(formData.username, formData.password);
      if (!error) {
        toast({
          title: 'Welcome back!',
          description: 'Successfully logged in.',
        });
        // Navigation will happen automatically via useEffect when profile is set
      } else {
        toast({
          title: 'Login Failed',
          description: error.message || 'Invalid credentials',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Login Failed',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Only redirect if we're on the login page and user successfully logs in
  React.useEffect(() => {
    // Only redirect if we're actually on the login page
    const currentPath = window.location.pathname;
    if (currentPath !== '/login' && currentPath !== '/patient/login') {
      return; // Don't redirect if we're not on a login page
    }
    
    if (profile) {
      console.log('LoginPage: Profile detected, redirecting based on role:', profile.role);
      switch (profile.role) {
        case 'patient':
          navigate('/patient/dashboard', { replace: true });
          break;
        case 'doctor':
        case 'chief_doctor':
          navigate('/doctor/dashboard', { replace: true });
          break;
        case 'admin':
          // Admin should use /admin/login, redirect them there
          navigate('/admin/login', { replace: true });
          break;
        case 'clinic_login':
          navigate('/clinic/dashboard', { replace: true });
          break;
        default:
          navigate('/', { replace: true });
      }
    }
  }, [profile, navigate]);

  return (
    <>
      <Helmet>
        <title>Sign In - Gugan Dental Care</title>
        <meta name="description" content="Sign in to your Gugan Dental Care account to access your dental practice management dashboard." />
      </Helmet>
      
      <div className="min-h-screen flex items-center justify-center p-3 sm:p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <Card className="glass-effect border-gray-700">
            <CardHeader className="text-center p-4 sm:p-6">
              <div className="flex items-center justify-center space-x-2 mb-3 sm:mb-4">
                <Stethoscope className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400" />
                <span className="text-xl sm:text-2xl font-bold gradient-text">Gugan Dental Care</span>
              </div>
              <CardTitle className="text-xl sm:text-2xl text-white">Welcome Back</CardTitle>
              <CardDescription className="text-gray-300 text-sm sm:text-base">
                Sign in to your account to continue
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-white">Username</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
                    {loadingUsers ? (
                      <Input
                        id="username"
                        placeholder="Loading users..."
                        className="pl-10 bg-gray-800 border-gray-600 text-white"
                        disabled
                      />
                    ) : (
                      <Select 
                        value={formData.username} 
                        onValueChange={(value) => setFormData({ ...formData, username: value })}
                      >
                        <SelectTrigger id="username" className="pl-10 bg-gray-800 border-gray-600 text-white">
                          <SelectValue placeholder="Select user to login" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-600">
                          {users.length > 0 ? (
                            users
                              .filter(user => {
                                // Only show active doctors/chief_doctors/clinic_login in dropdown (exclude admin)
                                // The API should already filter this, but add client-side check as well
                                return (user.status === 'Active' || !user.status) && user.role !== 'admin';
                              })
                              .sort((a, b) => {
                                // Sort order: chief_doctor first, then "Kaviyaa", then other doctors, clinic_login last
                                const aRole = a.role || '';
                                const bRole = b.role || '';
                                const aName = (a.full_name || a.username || '').toLowerCase().trim();
                                const bName = (b.full_name || b.username || '').toLowerCase().trim();
                                
                                // Clinic login always last
                                if (aRole === 'clinic_login' && bRole !== 'clinic_login') return 1;
                                if (bRole === 'clinic_login' && aRole !== 'clinic_login') return -1;
                                if (aRole === 'clinic_login' && bRole === 'clinic_login') return aName.localeCompare(bName);
                                
                                // Chief doctor first
                                if (aRole === 'chief_doctor' && bRole !== 'chief_doctor') return -1;
                                if (bRole === 'chief_doctor' && aRole !== 'chief_doctor') return 1;
                                
                                // If both are chief_doctor, maintain original order
                                if (aRole === 'chief_doctor' && bRole === 'chief_doctor') return 0;
                                
                                // "Kaviyaa" second (case-insensitive - check for various spellings)
                                // Check if name contains kaviyaa/kaaviya/kaviya (all lowercase since aName is already lowercased)
                                // Also check for exact matches and variations
                                const kaviyaaPatterns = ['kaviyaa', 'kaaviya', 'kaviya'];
                                const isKaviyaaA = kaviyaaPatterns.some(pattern => aName.includes(pattern)) || aName === 'kaviyaa' || aName === 'kaaviya';
                                const isKaviyaaB = kaviyaaPatterns.some(pattern => bName.includes(pattern)) || bName === 'kaviyaa' || bName === 'kaaviya';
                                
                                // If one is Kaviyaa and the other is not, Kaviyaa comes first (after chief_doctor)
                                if (isKaviyaaA && !isKaviyaaB) return -1;
                                if (isKaviyaaB && !isKaviyaaA) return 1;
                                
                                // If both are Kaviyaa, maintain original order
                                if (isKaviyaaA && isKaviyaaB) return 0;
                                
                                // Other doctors after (alphabetical order)
                                return aName.localeCompare(bName);
                              })
                              .map((user) => {
                                const displayName = (user.full_name || user.username || '').toUpperCase();
                                return (
                                  <SelectItem 
                                    key={user.id} 
                                    value={user.username}
                                    className="text-white hover:bg-gray-700"
                                  >
                                    {displayName}
                                  </SelectItem>
                                );
                              })
                          ) : (
                            <SelectItem value="" disabled className="text-gray-500">
                              No users available
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-white">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-10 pr-10 bg-gray-800 border-gray-600 text-white"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 h-4 w-4 text-gray-400 hover:text-gray-300 focus:outline-none"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full medical-gradient text-white hover:opacity-90"
                  disabled={loading}
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link 
                  to="/" 
                  className="text-blue-400 hover:text-blue-300 underline transition-colors text-sm"
                >
                  ← Back to Home
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </>
  );
};

export default LoginPage;